import { classNames } from 'libs/react';
import { useEffect } from 'libs/react';
import { useState, usePersistedState } from 'libs/react';
import { toggle } from 'libs/util/boolean';
import { TableFilter } from './private/TableFilter';
import { TableSearch } from './private/TableSearch';
import { AdditionialInformationButton } from 'components/AdditionalInformationButton';
import { Button } from 'components/Button';
import { ExportButton } from 'components/ExportButton';
import { ImportButton } from 'components/ImportButton';
import { MapButton } from 'components/MapButton';
import { SendEmailButton } from 'components/SendEmailButton';
import { Breakpoint } from 'components/util/Breakpoint';
import { Closeable } from 'components/util/Closeable';
import { React } from 'libs/react';
import { FormSelect } from 'components/form/FormSelect';
import { PricesUpdateButton } from 'components/PricesUpdateButton';
import './TableActions.scss';
/**
 * @component TableActions
 * @since 1.0.0
 */
export function TableActions(props) {
    const { style, filterable, filters, searchable, mappable, mappableEntity, entityId, pricesUpdate, optionConfiguration, importable, exportable, importMutation, exportMutation, exportType, statusType, refetchQuery, refetchQueryFieldName, refetchQueryVariables, filterListField, filterListValue, isSaleItem, itemOnly, optionOnly, manufacturing, isContent, isEndClient, selectOptions, selectName, selectPlaceholder, selectOnChange, sendEmail, sendEmailsMutation, isSaleAgreement, additionalInformation, onAdditionalInformationFilterApply, additionalInformationFilteredArray, defaultAdditionalInformationFilteredIds, additionalInformationFilteredIds, cardDisplay, isCardDisplay, setIsCardDisplay, quickFilter, tagIds } = Object.assign({ style: 'normal' }, props);
    // Generate a unique storage key based on filterListField and filterListValue
    const storageKey = React.useMemo(() => {
        const keyParts = ['table-actions'];
        if (filterListField) {
            keyParts.push(filterListField);
        }
        if (filterListValue) {
            const valueStr = Array.isArray(filterListValue)
                ? filterListValue.join(',')
                : String(filterListValue);
            keyParts.push(valueStr);
        }
        return keyParts.join('.');
    }, [filterListField, filterListValue]);
    const [search, setSearch] = usePersistedState(`${storageKey}.search`, '');
    const [filter, setFilter] = usePersistedState(`${storageKey}.filter`, {
        default: [],
        fields: []
    });
    const [operator, setOperator] = usePersistedState(`${storageKey}.operator`, 'AND');
    const [selectValue, setSelectValue] = useState('');
    const [opened, setOpened] = useState(false);
    /**
     * @function onFilter
     * @since 1.0.0
     */
    function onFilter(value, operator) {
        setFilter(value);
        setOperator(operator);
    }
    /**
     * @function onSearch
     * @since 1.0.0
     */
    function onSearch(search) {
        setSearch(search);
    }
    /**
     * @function onClick
     * @since 1.0.0
     */
    function onClick() {
        setOpened(toggle);
    }
    /**
     * @function onClose
     * @since 1.0.0
     */
    function onClose() {
        setOpened(false);
    }
    /**
     * @function onClickClear
     * @since 1.0.0
     */
    const onClickClear = () => {
        props.onClickClear && props.onClickClear();
        setSelectValue('');
    };
    /**
     * @function onChange
     * @since 1.0.0
     */
    const onChange = (value) => {
        selectOnChange && selectOnChange(value);
        setSelectValue(value);
    };
    const onPressList = () => {
        setIsCardDisplay && setIsCardDisplay(false);
    };
    const onPressCard = () => {
        setIsCardDisplay && setIsCardDisplay(true);
    };
    useEffect(() => {
        if (props.onSearch) {
            props.onSearch(search);
        }
    }, [search]);
    useEffect(() => {
        if (props.onFilter) {
            props.onFilter(filter, operator);
        }
    }, [filter, operator]);
    const className = classNames('table-actions', 'table-actions--' + style, { 'table-actions--is-opened': opened }, props.className);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: className },
            React.createElement("div", { className: "table-actions-body" },
                filterable && (React.createElement("div", { className: "table-actions-item" },
                    React.createElement(TableFilter, { field: filterListField, value: filterListValue, filterable: filterable, onFilter: onFilter, filters: filters, initialFilter: filter, initialOperator: operator }))),
                searchable && (React.createElement("div", { className: "table-actions-item" },
                    React.createElement(TableSearch, { onSearch: onSearch, defaultValue: search }))),
                additionalInformation &&
                    onAdditionalInformationFilterApply &&
                    additionalInformationFilteredArray &&
                    defaultAdditionalInformationFilteredIds &&
                    additionalInformationFilteredIds && (React.createElement("div", { className: "table-actions-item" },
                    React.createElement(AdditionialInformationButton, { onAdditionalInformationFilterApply: onAdditionalInformationFilterApply, additionalInformationFilteredArray: additionalInformationFilteredArray, defaultAdditionalInformationFilteredIds: defaultAdditionalInformationFilteredIds, additionalInformationFilteredIds: additionalInformationFilteredIds }))),
                isContent && selectOptions && selectName && selectPlaceholder && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "table-actions-item" },
                        React.createElement(FormSelect, { name: selectName, placeholder: selectPlaceholder, options: selectOptions, onChange: onChange, value: selectValue, size: "small" })),
                    selectValue && (React.createElement("div", { className: "table-actions-item" },
                        React.createElement(Button, { size: "small", style: "picto", picto: "xmark", onPress: onClickClear })))))),
            React.createElement("div", { className: "table-actions-foot" },
                React.createElement("div", { className: "table-actions-menu" },
                    React.createElement(Closeable, { onClose: onClose },
                        React.createElement(Breakpoint, { isBelow: "xl" },
                            React.createElement(Button, { className: "table-actions-menu-toggle", style: "picto", picto: "ellipsis", onPress: onClick })),
                        React.createElement("div", { className: "table-actions-menu-items" },
                            sendEmail && sendEmailsMutation && (React.createElement("div", { className: "table-actions-item" },
                                React.createElement(SendEmailButton, { sendEmailsMutation: sendEmailsMutation }))),
                            mappable && (React.createElement("div", { className: "table-actions-item" },
                                React.createElement(MapButton, { label: t('Map'), entity: mappableEntity }))),
                            pricesUpdate && optionConfiguration && (React.createElement(PricesUpdateButton, { label: t('Update Prices'), optionConfiguration: optionConfiguration })),
                            importable && importMutation && exportMutation && (React.createElement("div", { className: "table-actions-item" },
                                React.createElement(ImportButton, { label: t('Import'), importMutation: importMutation, importSuccess: props.importSuccess, templateMutation: exportMutation, refetchQuery: refetchQuery, refetchQueryFieldName: refetchQueryFieldName, refetchQueryVariables: refetchQueryVariables, input: { search, tagIds }, isEndClient: isEndClient, isSaleItem: isSaleItem, entityId: entityId, optionOnly: optionOnly, itemOnly: itemOnly, manufacturing: manufacturing }))),
                            exportable && exportMutation && (React.createElement("div", { className: "table-actions-item" },
                                React.createElement(ExportButton, { label: t('Export'), exportMutation: exportMutation, exportType: exportType, statusType: statusType, input: { search, tagIds }, filter: filter, isSaleItem: isSaleItem, isSaleAgreement: isSaleAgreement, isEndClient: isEndClient, optionOnly: optionOnly, itemOnly: itemOnly, manufacturing: manufacturing }))),
                            cardDisplay && (React.createElement("div", { className: "table-actions-button-container" },
                                React.createElement(Button, { picto: "list", onPress: onPressList, className: `button-list ${!isCardDisplay && 'active'}` }),
                                React.createElement(Button, { picto: "fas cards-blank", onPress: onPressCard, className: `button-card ${isCardDisplay && 'active'}` })))))))),
        React.createElement("div", null, quickFilter && (React.createElement("div", { className: "table-actions-quick-filter" }, quickFilter)))));
}
/**
 * @function isTableSearchValueEmpty
 * @since 1.0.0
 */
export function isTableSearchValueEmpty(value) {
    return value.trim().length == 0;
}
/**
 * @function isTableFilterValueEmpty
 * @since 1.0.0
 */
export function isTableFilterValueEmpty(value) {
    return value.default.length == 0 && value.fields.length == 0;
}
